// 后台骨架 <AdminShell>（后台规范 §2，EP 紧凑档）：左侧栏 168（文员区 / 管理员区）+ 顶栏 58（页面标题、内网直连、开启提醒、账号）
// + 告警横幅（顶栏下方）+ toast；≤750px（手机 / PWA）侧栏收起，改为紧凑顶栏 + 底部标签栏 +「更多」面板
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAlerts } from "../lib/alerts";
import { isStaff, useAuth } from "../lib/auth";
import { ROLE_TEXT } from "../lib/format";
import { useNetMode } from "../lib/net";
import { useRealtime, useWsMessages } from "../lib/realtime";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { request } from "../lib/api";
import type { TaskDTO } from "@csp/shared";
import { LanguageSwitch } from "./LanguageSwitch";
import { applyUpdate, useUpdateAvailable } from "../lib/pwa";

/** 卡住任务数量（导航角标）：进入页面、收到任务事件（防抖）、每分钟刷新 */
function useBlockedCount(enabled: boolean): number {
  const [n, setN] = useState(0);
  const timer = useRef<number | null>(null);
  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      setN((await request<TaskDTO[]>("GET", "/api/tasks?status=blocked&limit=200")).length);
    } catch {
      /* 忽略 */
    }
  }, [enabled]);
  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(), 60_000);
    return () => window.clearInterval(id);
  }, [load]);
  useWsMessages((m) => {
    if ((m as { type?: string }).type !== "task") return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void load(), 800);
  });
  return n;
}

const BANNER_ICON: Record<string, string> = { scan: "📱", review: "🔍", blocked: "⛔", failed: "✖" };

/** 顶栏标题：按路由取（详情 / 修改等带参数的路由用前缀判断） */
const TITLES: [RegExp, string][] = [
  [/^\/$/, "任务"],
  [/^\/stats$/, "任务统计"],
  [/^\/tasks\/new$/, "新建任务"],
  [/^\/tasks\/[^/]+\/edit$/, "修改任务"],
  [/^\/tasks\/[^/]+$/, "任务详情"],
  [/^\/types\/new$/, "添加任务类型"],
  [/^\/admin$/, "健康面板"],
  [/^\/admin\/types$/, "任务类型"],
  [/^\/admin\/types\//, "任务类型详情"],
  [/^\/admin\/compare\//, "运行对比"],
  [/^\/admin\/test-tasks$/, "测试任务"],
  [/^\/admin\/agents-md$/, "AGENTS.md"],
  [/^\/admin\/users$/, "账号"],
  [/^\/admin\/system$/, "系统"],
];

export function pageTitle(pathname: string): string {
  return TITLES.find(([re]) => re.test(pathname))?.[1] ?? "CSP 任务后台";
}

function NavItem({ to, icon, end, match, children }: { to: string; icon: string; end?: boolean; match?: RegExp; children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <NavLink to={to} end={end} data-nav={to} className={({ isActive }) => `nav-item${isActive || match?.test(pathname) ? " active" : ""}`}>
      <span className="ico" aria-hidden="true">
        {icon}
      </span>
      {children}
    </NavLink>
  );
}

interface NavDef {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
  /** 这些页面也算在这一项下（页内标签、页内按钮进去的页面），高亮这一项 */
  match?: RegExp;
  /** 放在手机底部标签栏；其余的放进「更多」 */
  tab?: boolean;
}

/**
 * 导航项（侧栏与手机「更多」共用）。2026-10-30 业主定整理：
 * 任务队列 + 全部任务合成「任务」（新建任务、任务统计做成页内按钮；探索 / dry run / 批量测试的任务不在这里，在管理员区「测试任务」）；
 * 新建任务类型只给管理员；并发测试、管理员队列、待我审核、AI 建议已去掉（同日业主定：等审核看任务页统计卡 / 需要优先处理，
 * SOP 改动走类型详情「SOP 补充」，单次运行的提议在任务详情里）；
 * 健康面板是「任务类型」的页内标签，账号 / AGENTS.md 是「系统」的页内标签
 */
function navGroups(staff: boolean, admin: boolean): { group: string; items: NavDef[] }[] {
  const clerk: NavDef[] = [
    { to: "/", icon: "▤", label: "任务", end: true, tab: true, match: /^\/(tasks\/|stats$)/ },
  ];
  if (staff) clerk.push({ to: "/sponsor-mails", icon: "✉", label: "担保人邮箱", tab: true });
  const groups = [{ group: "文员区", items: clerk }];
  if (admin)
    groups.push({
      group: "管理员区",
      items: [
        { to: "/admin/types", icon: "◫", label: "任务类型", match: /^\/(admin|admin\/types\/.*|admin\/compare\/.*|types\/new)$/ },
        { to: "/admin/test-tasks", icon: "⚗", label: "测试任务" },
        { to: "/admin/system", icon: "⚙", label: "系统", match: /^\/admin\/(users|agents-md)$/ },
      ],
    });
  return groups;
}

function BlockedCount({ n, testId = "nav-blocked" }: { n: number; testId?: string }) {
  if (n <= 0) return null;
  return (
    <span className="tab-count" title={`${n} 个任务卡住`} data-testid={testId}>
      {n}
    </span>
  );
}

/** 有新版本但没自动刷新（正在用的时候发布了）：顶部一条提示，点了刷新 */
function UpdateBar() {
  const available = useUpdateAvailable();
  if (!available) return null;
  return (
    <div className="update-bar" role="status" data-testid="update-bar">
      <span>有新版本</span>
      <button className="btn btn-small btn-primary" onClick={applyUpdate}>
        立即更新
      </button>
    </div>
  );
}

export function AdminShell() {
  const { user, logout } = useAuth();
  const net = useNetMode();
  const rt = useRealtime();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const staff = isStaff(user?.role);
  const admin = user?.role === "admin";
  const blocked = useBlockedCount(!!user);
  const groups = navGroups(staff, admin);
  const tabs = groups.flatMap((g) => g.items).filter((i) => i.tab);
  const more = groups.map((g) => ({ ...g, items: g.items.filter((i) => !i.tab) })).filter((g) => g.items.length);
  const [sheet, setSheet] = useState(false);
  // 换页就收起「更多」
  useEffect(() => setSheet(false), [pathname]);
  const signOut = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <>
      <aside id="sidebar">
        <div className="brand">CSP 任务后台</div>
        <nav aria-label="主导航" style={{ display: "contents" }}>
          {groups.map((g) => (
            <Fragment key={g.group}>
              <div className="nav-group">{g.group}</div>
              {g.items.map((i) => (
                <NavItem key={i.to} to={i.to} icon={i.icon} end={i.end} match={i.match}>
                  {i.label}
                  {i.to === "/" && <BlockedCount n={blocked} />}
                </NavItem>
              ))}
            </Fragment>
          ))}
        </nav>
        <div className="sgt">新加坡时间 SGT · UTC+8</div>
      </aside>
      <nav className="mtabs" aria-label="手机导航" data-testid="mobile-tabs">
        {tabs.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} data-nav={i.to} className={({ isActive }) => `mtab${isActive || i.match?.test(pathname) ? " active" : ""}`}>
            <span className="ico" aria-hidden="true">
              {i.icon}
            </span>
            <span className="mtab-label">{i.label}</span>
            {i.to === "/" && <BlockedCount n={blocked} testId="mtab-blocked" />}
          </NavLink>
        ))}
        <button type="button" className={`mtab${sheet ? " active" : ""}`} onClick={() => setSheet((v) => !v)} aria-expanded={sheet}>
          <span className="ico" aria-hidden="true">
            ⋯
          </span>
          <span className="mtab-label">更多</span>
        </button>
      </nav>
      {sheet && (
        <div className="msheet-mask" onClick={() => setSheet(false)}>
          <div className="msheet" role="dialog" aria-label="更多" onClick={(e) => e.stopPropagation()} data-testid="mobile-sheet">
            <div className="msheet-user">
              <span translate="no">{user?.displayName}</span>
              <span className="muted">（{user ? ROLE_TEXT[user.role] : ""}）</span>
            </div>
            {more.map((g) => (
              <Fragment key={g.group}>
                {more.length > 1 && <div className="nav-group">{g.group}</div>}
                {g.items.map((i) => (
                  <NavItem key={i.to} to={i.to} icon={i.icon} end={i.end} match={i.match}>
                    {i.label}
                  </NavItem>
                ))}
              </Fragment>
            ))}
            <div className="msheet-foot">
              <span className="muted small">
                新加坡时间 SGT · 版本 <span translate="no">{__CSP_BUILD__.slice(0, 7)}</span>
              </span>
              <span className="spacer" />
              <button className="btn btn-small" onClick={() => void signOut()}>
                退出登录
              </button>
            </div>
          </div>
        </div>
      )}
      <header className="topbar">
        <span className="title">{pageTitle(pathname)}</span>
        <div className="right">
          <LanguageSwitch />
          <span className={`net hide-m ${net}`} title={net === "lan" ? "正在走办公室内网，画面更快" : "正在走外网"} data-testid="net-mode">
            <span className="dot" />
            {net === "lan" ? "内网直连" : "外网"}
          </span>
          {!rt.connected && <span className="err" title="实时通道断开，正在重连">● 实时连接中断</span>}
          {!alerts.enabled && (
            <button className="btn btn-small btn-warn" onClick={() => void alerts.enable()}>
              🔔 开启提醒
            </button>
          )}
          <span className="hide-m">
            <span translate="no">{user?.displayName}</span>
            <span className="muted">（{user ? ROLE_TEXT[user.role] : ""}）</span>
          </span>
          <button className="btn btn-small hide-m" onClick={() => void signOut()}>
            退出登录
          </button>
        </div>
      </header>
      <UpdateBar />
      {alerts.banners.length > 0 && (
        <div className="banners" data-testid="banners">
          {alerts.banners.map((b) => (
            <div key={b.id} className={`banner ${b.level}`} data-level={b.level} onClick={() => alerts.openBanner(b)} role="alert">
              <span>{BANNER_ICON[b.level] ?? "!"}</span>
              <span>{b.text}</span>
              <span className="small" style={{ opacity: 0.85 }}>点这里打开 →</span>
              <button
                className="x"
                aria-label="关闭"
                onClick={(e) => {
                  e.stopPropagation();
                  alerts.dismissBanner(b.id);
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <main className="page">
        <Outlet />
      </main>
      <div className="toasts">
        {alerts.toasts.map((t) => (
          <div
            key={t.id}
            className={`toast ${t.kind}`}
            role="status"
            data-testid="toast"
            onClick={() => {
              alerts.dismissToast(t.id);
              if (t.taskId) navigate(`/tasks/${t.taskId}`);
            }}
          >
            {t.kind === "ok" ? "✅ " : ""}
            {t.text}
            {t.action && (
              <button
                type="button"
                className="toast-action"
                onClick={(e) => {
                  e.stopPropagation();
                  alerts.dismissToast(t.id);
                  t.action?.run();
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
